import { pick } from 'lodash';
import { FilterWithOperatorProps } from '../types';
import { APIFieldType, Operator } from '@ardoq/api-types';
import {
  DATE_OPERATOR_TO_LABEL,
  DEFAULT_OPERATOR_TO_LABEL,
} from '@ardoq/query-builder';
import { Select, SelectOption } from '@ardoq/select';
import { isEmptyOptionSelected, isBetweenOptionSelected } from '../utils';
import {
  RelativeDatePicker,
  RelativeDatePickerMode,
} from '@ardoq/date-time-picker';
import { RemoveFilterButtonInTopRightCorner, SelectContainer } from './atoms';
import { Maybe } from '@ardoq/common-helpers';
import {
  removeExcludedOperators,
  isDateStringInterval,
  isDateString,
} from './filterUtils';
import { isDateRangeReversed } from '@ardoq/date-time';
import type { DateColumnFilterInputLabels } from '../labels';

export type { DateColumnFilterInputLabels };

const OPERATOR_SELECT_OPTIONS = Object.entries({
  ...pick({ ...DEFAULT_OPERATOR_TO_LABEL, ...DATE_OPERATOR_TO_LABEL }, [
    Operator.EQUAL,
    Operator.LESS,
    Operator.GREATER,
    Operator.BETWEEN_INCLUSIVE,
    Operator.IS_EMPTY,
    Operator.IS_NOT_EMPTY,
  ]),
}).map(([operator, label]) => ({
  label,
  value: operator,
})) as SelectOption<Operator>[];

const DEFAULT_LABELS = {
  startDatePlaceholder: 'Start date',
  endDatePlaceholder: 'End date',
  startHigherError:
    'Start date should be before end date. Enter a valid range.',
} satisfies Required<DateColumnFilterInputLabels>;

const DEFAULT_REMOVE_FILTER_BUTTON_LABEL = 'Remove filter';

const DateInputCombination = ({
  column,
  expression: { value, operator },
  isReadOnly,
  changeFilterExpression,
  labels,
}: FilterWithOperatorProps & {
  labels: Required<DateColumnFilterInputLabels>;
}) => {
  if (isEmptyOptionSelected(operator)) return null;

  const mode =
    column.dataType === APIFieldType.DATE_ONLY
      ? RelativeDatePickerMode.DATE
      : RelativeDatePickerMode.DATE_TIME;

  if (isBetweenOptionSelected(operator)) {
    const { start, end } = isDateStringInterval(value)
      ? value
      : { start: null, end: null };
    const isStartHigher =
      start && end ? isDateRangeReversed({ start, end }) : false;

    return (
      <>
        <RelativeDatePicker
          value={start ?? undefined}
          placeholder={labels.startDatePlaceholder}
          onChange={newValue => {
            changeFilterExpression(column, {
              value: {
                start: newValue ?? null,
                end,
              },
              operator,
            });
          }}
          relativeDatePickerMode={mode}
          errorMessage={isStartHigher ? '' : undefined}
          hasError={isStartHigher}
          isDisabled={isReadOnly}
        />

        <RelativeDatePicker
          value={end ?? undefined}
          placeholder={labels.endDatePlaceholder}
          onChange={newValue => {
            changeFilterExpression(column, {
              value: {
                start,
                end: newValue ?? null,
              },
              operator,
            });
          }}
          relativeDatePickerMode={mode}
          errorMessage={isStartHigher ? labels.startHigherError : undefined}
          hasError={isStartHigher}
          isDisabled={isReadOnly}
        />
      </>
    );
  }

  return (
    <RelativeDatePicker
      value={isDateString(value) ? value : undefined}
      onChange={newValue =>
        changeFilterExpression(column, {
          value: newValue ?? null,
          operator,
        })
      }
      relativeDatePickerMode={mode}
      isDisabled={isReadOnly}
    />
  );
};

type DateColumnFilterInputProps = FilterWithOperatorProps & {
  menuTarget: Maybe<HTMLElement>;
};

export const DateColumnFilterInput = ({
  column,
  expression,
  changeFilterExpression,
  removeFilter,
  menuTarget,
  isReadOnly,
  excludedOperators,
  labels,
  removeFilterButtonLabel = DEFAULT_REMOVE_FILTER_BUTTON_LABEL,
}: DateColumnFilterInputProps & {
  labels?: DateColumnFilterInputLabels;
  removeFilterButtonLabel?: string;
}) => {
  const mergedLabels = { ...DEFAULT_LABELS, ...labels };
  return (
    <SelectContainer>
      {!isReadOnly && (
        <RemoveFilterButtonInTopRightCorner
          onClick={() => removeFilter(column.key)}
          label={removeFilterButtonLabel}
        />
      )}
      <Select
        label={column.label}
        options={removeExcludedOperators(
          OPERATOR_SELECT_OPTIONS,
          excludedOperators
        )}
        value={expression.operator}
        onChange={operatorOption => {
          let correctedValue = isEmptyOptionSelected(operatorOption?.value)
            ? null
            : expression.value;

          // change to BETWEEN operator
          if (
            isBetweenOptionSelected(operatorOption?.value) &&
            !isBetweenOptionSelected(expression.operator)
          ) {
            correctedValue = {
              start: isDateString(expression.value) ? expression.value : null,
              end: null,
            };
          } else if (
            // change from BETWEEN operator
            !isEmptyOptionSelected(operatorOption?.value) &&
            !isBetweenOptionSelected(operatorOption?.value) &&
            isBetweenOptionSelected(expression.operator)
          ) {
            correctedValue = isDateStringInterval(expression.value)
              ? expression.value.start
              : null;
          }

          changeFilterExpression(column, {
            value: correctedValue,
            operator: operatorOption?.value ?? Operator.EQUAL,
          });
        }}
        menuPortalTarget={menuTarget}
        isReadOnly={isReadOnly}
      />
      <DateInputCombination
        column={column}
        expression={expression}
        changeFilterExpression={changeFilterExpression}
        removeFilter={removeFilter}
        isReadOnly={isReadOnly}
        labels={mergedLabels}
      />
    </SelectContainer>
  );
};
